import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { STAKE_ABI } from "@/contract/abi/stake";
import ContractAddress from "@/contract/addresses";
import { STAKE_CONSTANTS } from "@/lib/stake/stake-constants";

/**
 * Mount-time sanity check.
 * Reads 8 on-chain constants and compares them to the STAKE_CONSTANTS mirror.
 * If any value drifted (contract redeployed with different params), disables UI.
 */
export function useStakeConstants(): {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: Error;
} {
  const publicClient = usePublicClient();

  const { isLoading, isSuccess, isError, error } = useQuery({
    queryKey: ["stake", "constants"],
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    enabled: !!publicClient,
    queryFn: async () => {
      if (!publicClient) throw new Error("No public client");

      const reads = await publicClient.multicall({
        contracts: [
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "STAKE_MAX_DNM",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "STAKE_MAX_UVM",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "STAKE_MIN_DNM",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "REWARD_DECAY_FACTOR",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "REWARD_DECAY_PERIOD",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "ONE_YEAR_DURATION",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "EIGHTEEN_MONTH_DURATION",
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "TWO_YEAR_DURATION",
          },
        ],
        allowFailure: false,
      });

      const [maxDnm, maxUvm, minDnm, decay, decayPeriod, y1, y18, y24] = reads;
      const expected = STAKE_CONSTANTS;

      if (
        maxDnm !== expected.STAKE_MAX_DNM_WEI ||
        maxUvm !== expected.STAKE_MAX_UVM_WEI ||
        minDnm !== expected.STAKE_MIN_DNM_WEI ||
        decay !== expected.REWARD_DECAY_FACTOR ||
        decayPeriod !== expected.REWARD_DECAY_PERIOD_SECONDS ||
        y1 !== expected.ONE_YEAR_SECONDS ||
        y18 !== expected.EIGHTEEN_MONTH_SECONDS ||
        y24 !== expected.TWO_YEAR_SECONDS
      ) {
        console.error("[STAKE-01] Stake constants drift", {
          on_chain: reads,
          expected,
        });
        throw new Error(
          "Stake constants drift — frontend mirror out of sync with contract. UI disabled.",
        );
      }

      return { ok: true };
    },
  });

  return {
    isLoading,
    isSuccess,
    isError,
    error: isError ? (error as Error) : undefined,
  };
}
